import { CommandCenterHeader } from "@components";
import { Role } from "@enums";
import {
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AutomationServerDetailOut,
  ConsumerInfoOut,
  CustomerAutopintOut,
  useAutomationIntegrationDeploymentApiV1GetConsumerOverviewQuery,
  useWebApiV1GetUserPreferencesQuery,
} from "@providers/hop-ord-server/api";
import { skipToken } from "@reduxjs/toolkit/query";
import { toLocaleDateTimeString } from "@utils";
import { useCheckUserRole } from "@utils/useCheckUserRole";
import { useNavigate } from "react-router-dom";
import IdleTimeIndicator from "./components/IdleTimeIndicator";
import { tableStyles } from "./components/utils";
import StatusRow from "./components/StatusRow";
import { useHealthcheck } from "./hooks";
import { useState } from "react";
import dayjs from "dayjs";

const borderRightStyle = {
  borderRight: "1px solid",
  borderRightColor: "divider",
};

const borderLeftStyle = {
  borderLeft: "1px solid",
  borderLeftColor: "divider",
};

const getHealthcheckStatusMsg = (
  deploymentInfo: CustomerAutopintOut,
  healthcheck: AutomationServerDetailOut | undefined,
) => {
  const numConsumers =
    deploymentInfo.consumers.planGenConsumers.length +
    deploymentInfo.consumers.otherConsumers.length;

  if (deploymentInfo.connected) {
    if (healthcheck === undefined) {
      return "LP is connected to Redis, but AP has not responded with a valid health check response.";
    }
    if (healthcheck.message === "Success") {
      if (!healthcheck.isLatest) {
        return "Health check is out of date.";
      }
      return "Healthy";
    }
    if (healthcheck.message) {
      return (
        <>
          <p>
            LP is connected to Redis, but AP encountered an error responding to
            the health check request.
          </p>
          <p>Message: {healthcheck.message}</p>
        </>
      );
    }
    if (numConsumers > 0) {
      return "LP is connected to Redis, but it appears AP's consumers were not able to respond to the health check request.";
    }
    return "LP is connected to Redis, but it appears AP's consumers aren't up.";
  }
  return "LP is not connected to Redis. Cannot perform health check.";
};

const isConsumerDead = (consumer: ConsumerInfoOut) => {
  return consumer.idleMillis > dayjs.duration(1, "week").asMilliseconds();
};

const DeploymentDashboardPage = () => {
  const navigate = useNavigate();
  const { data: userPreferenceData } = useWebApiV1GetUserPreferencesQuery();
  const tenantIds = userPreferenceData?.tenant_ids;
  const shouldQueryConsumerInfo = !!tenantIds;

  const { healthcheckData, isHealthcheckLoading, doHealthcheck } =
    useHealthcheck(tenantIds ?? [], !shouldQueryConsumerInfo);

  const {
    data: deploymentInfo,
    isLoading: deploymentInfoLoading,
    isFetching: deploymentInfoFetching,
    refetch: refetchDeploymentInfo,
  } = useAutomationIntegrationDeploymentApiV1GetConsumerOverviewQuery(
    shouldQueryConsumerInfo ? { tenantIds } : skipToken,
  );

  const [showStaleConsumers, setShowStaleConsumers] = useState(false);

  const hasDeploymentAccess = useCheckUserRole(Role.AUTOMATION_DEPLOYMENT);

  if (!hasDeploymentAccess) {
    navigate("/error");
  }

  return (
    <Stack
      sx={{
        height: 1,
        width: 1,
        padding: 2,
        gap: 1,
      }}
    >
      <CommandCenterHeader title="Deployment Dashboard" />
      <Paper variant="outlined" sx={{ padding: 2, flexGrow: 1 }}>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
              <Typography variant="h6">Consumer Overview</Typography>
              <IconButton
                onClick={refetchDeploymentInfo}
                loading={deploymentInfoFetching}
              >
                <RefreshIcon
                  sx={{
                    visibility: deploymentInfoFetching ? "hidden" : "visible",
                  }}
                />
              </IconButton>
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={showStaleConsumers}
                  onChange={() => setShowStaleConsumers(!showStaleConsumers)}
                />
              }
              label="Show stale consumers"
            />
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table sx={{ minWidth: 650, ...tableStyles }}>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={3} />
                  <TableCell
                    colSpan={2}
                    align="center"
                    sx={{
                      borderStyle: "solid",
                      borderWidth: "0 1px 1px",
                      borderColor: "divider",
                    }}
                  >
                    Planning Consumers
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    align="center"
                    sx={{
                      borderStyle: "solid",
                      borderWidth: "0 1px 1px",
                      borderColor: "divider",
                    }}
                  >
                    General Consumers
                  </TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell width="5%">Customer</TableCell>
                  <TableCell width="5%">TPS</TableCell>
                  <TableCell width="5%" align="center">
                    Connected
                  </TableCell>
                  <TableCell width="10%" sx={borderLeftStyle}>
                    Status
                  </TableCell>
                  <TableCell width="5%" sx={borderRightStyle}>
                    Pressure
                  </TableCell>
                  <TableCell width="20%" sx={borderLeftStyle}>
                    Status
                  </TableCell>
                  <TableCell width="5%" sx={borderRightStyle}>
                    Pressure
                  </TableCell>
                  <TableCell width="15%">Last Updated</TableCell>
                  <TableCell>
                    <Stack
                      direction="row"
                      sx={{
                        gap: 1,
                      }}
                    >
                      Health check
                      <IconButton
                        onClick={() => doHealthcheck()}
                        loading={isHealthcheckLoading}
                        sx={{ padding: 0 }}
                      >
                        <RefreshIcon
                          sx={{
                            visibility: isHealthcheckLoading
                              ? "hidden"
                              : "visible",
                          }}
                        />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!deploymentInfoLoading &&
                  deploymentInfo &&
                  [...deploymentInfo]
                    .sort((a, b) => a.tenantId - b.tenantId)
                    .map((deploymentInfo, idx) => {
                      const healthcheck =
                        healthcheckData?.[deploymentInfo.tenantId];
                      const healthy = healthcheck?.message === "Success";
                      const lastHealthcheckAt = healthcheck?.lastHealthcheckAt
                        ? toLocaleDateTimeString(healthcheck.lastHealthcheckAt)
                        : "-";
                      const planGenGroupLag =
                        deploymentInfo.groups.planGenGroups.reduce(
                          (acc, group) => acc + group.lag,
                          0,
                        );
                      const otherGroupLag =
                        deploymentInfo.groups.otherGroups.reduce(
                          (acc, group) => acc + group.lag,
                          0,
                        );

                      return (
                        <TableRow
                          hover
                          key={idx}
                          sx={{ cursor: "pointer" }}
                          onClick={() =>
                            navigate(`/deployments/${deploymentInfo.tenantId}`)
                          }
                        >
                          <TableCell>
                            {deploymentInfo.tenantName.replace("tenant:", "")}
                          </TableCell>
                          <TableCell>{deploymentInfo.tps}</TableCell>
                          <TableCell align="center">
                            {deploymentInfo.connected ? (
                              <Chip
                                label="Yes"
                                color="success"
                                size="small"
                                sx={{ color: "inherit" }}
                              />
                            ) : (
                              <Chip
                                label="No"
                                color="error"
                                size="small"
                                sx={{ color: "inherit" }}
                              />
                            )}
                          </TableCell>
                          {deploymentInfo.error ? (
                            <Tooltip
                              title={
                                deploymentInfo.connected
                                  ? "LP is connected to Redis, but it appears that there are no consumers."
                                  : "LP is not connected to Redis."
                              }
                              arrow
                            >
                              <TableCell
                                colSpan={4}
                                align="center"
                                sx={{
                                  backgroundColor: "error.light",
                                  ...borderLeftStyle,
                                  ...borderRightStyle,
                                }}
                              >
                                <Typography color="error">
                                  {deploymentInfo.error}
                                </Typography>
                              </TableCell>
                            </Tooltip>
                          ) : (
                            <>
                              <TableCell sx={borderLeftStyle}>
                                <Stack direction="row">
                                  {deploymentInfo.consumers.planGenConsumers.map(
                                    (consumer, idx) => {
                                      if (
                                        !showStaleConsumers &&
                                        isConsumerDead(consumer)
                                      ) {
                                        return null;
                                      }
                                      if (deploymentInfo.groups)
                                        return (
                                          <IdleTimeIndicator
                                            consumer={consumer}
                                            key={idx}
                                            alert={
                                              consumer.idleMillis >
                                                dayjs
                                                  .duration(1, "hour")
                                                  .asMilliseconds() &&
                                              planGenGroupLag > 0
                                            }
                                          />
                                        );
                                    },
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell sx={borderRightStyle}>
                                {planGenGroupLag}
                              </TableCell>
                              <TableCell sx={borderLeftStyle}>
                                <Stack direction="row">
                                  {deploymentInfo.consumers.otherConsumers.map(
                                    (consumer, idx) => {
                                      if (
                                        !showStaleConsumers &&
                                        isConsumerDead(consumer)
                                      ) {
                                        return null;
                                      }
                                      return (
                                        <IdleTimeIndicator
                                          consumer={consumer}
                                          key={idx}
                                          pressure={otherGroupLag > 0}
                                          alert={otherGroupLag > 0}
                                        />
                                      );
                                    },
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell sx={borderRightStyle}>
                                {otherGroupLag}
                              </TableCell>
                            </>
                          )}
                          <TableCell>
                            {toLocaleDateTimeString(deploymentInfo.lastUpdated)}
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              sx={{ gap: 1, alignItems: "center" }}
                            >
                              <Tooltip
                                title={getHealthcheckStatusMsg(
                                  deploymentInfo,
                                  healthcheck,
                                )}
                                arrow
                              >
                                <Avatar
                                  sx={{
                                    height: 26,
                                    width: 26,
                                    bgcolor: healthy
                                      ? "success.main"
                                      : "error.main",
                                  }}
                                >
                                  {healthy ? (
                                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                                  ) : (
                                    <ErrorIcon sx={{ fontSize: 20 }} />
                                  )}
                                </Avatar>
                              </Tooltip>
                              <Stack>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    p: 0,
                                    m: 0,
                                  }}
                                >
                                  Last check: {lastHealthcheckAt}
                                </Typography>
                                <Typography variant="caption">
                                  Version: {healthcheck?.appVersion ?? "-"}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                {deploymentInfoLoading && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <StatusRow
                        message="Loading deployment info..."
                        type="loading"
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default DeploymentDashboardPage;
